/**
 * Ficha de un episodio de recobro — la pantalla que sustituye a la fila del
 * Sheets (roadmap §F2.b).
 *
 * La pieza central es la **timeline combinada**: los envíos programados
 * (`scheduledSends`) y las decisiones del motor (`decisionLog`) en un único
 * hilo cronológico. Separarlas obligaría a Ops a cruzar dos listas a ojo para
 * responder a la pregunta real ("¿por qué a este cliente no le ha llegado
 * nada?"), que casi siempre se contesta con una decisión `shadow`/`suppress`
 * intercalada entre dos envíos.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  createEpisodeNote,
  getEpisode,
  pauseEpisode,
  resumeEpisode,
  useMsOpsUnavailable,
} from "../../api/msops";
import { useAuth } from "../../context/AuthContext";
import {
  CHANNEL_LABEL,
  DECISION_LABEL,
  DECISION_STYLES,
  DecisionKind,
  DecisionLogItem,
  EPISODE_STATUS_LABEL,
  EPISODE_STATUS_STYLES,
  EpisodeDetail as EpisodeDetailType,
  PROCESS_KIND_LABEL,
  ScheduledSendItem,
  SEND_STATUS_LABEL,
  SEND_STATUS_STYLES,
} from "../../types/msops";
import ActionsBar from "./components/ActionsBar";
import { resolveDispute } from "../../api/msops";
import {
  Badge,
  buttonStyle,
  COLORS,
  Card,
  describeError,
  fieldStyle,
  fmtDateTime,
  inputStyle,
  labelStyle,
  Loading,
  secondaryButtonStyle,
  UnavailableNotice,
} from "./components/ui";

/** Entrada unificada de la timeline: un envío o una decisión. */
type TimelineEntry =
  | { kind: "send"; at: string; send: ScheduledSendItem }
  | { kind: "decision"; at: string; decision: DecisionLogItem };

export default function EpisodeDetail() {
  const { episodeId } = useParams<{ episodeId: string }>();
  const { token } = useAuth();
  const unavailable = useMsOpsUnavailable();

  const [episode, setEpisode] = useState<EpisodeDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [noteBody, setNoteBody] = useState("");
  const [savingNote, setSavingNote] = useState(false);

  const [pauseOpen, setPauseOpen] = useState(false);
  /**
   * Resolver la disputa: `disputed` era un pestillo de una sola dirección —
   * se ponía con el chargeback y no lo quitaba nadie, así que el episodio
   * quedaba congelado para siempre (había cuatro clientes que ya no debían
   * nada llevando semanas así). El endpoint existía desde hoy sin UI.
   */
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [disputeOutcome, setDisputeOutcome] =
    useState<"ganada" | "perdida" | "reemitida" | "sin_disputa">("ganada");
  const [disputeReason, setDisputeReason] = useState("");
  const [motivo, setMotivo] = useState("");
  const [hasta, setHasta] = useState("");
  const [ticket, setTicket] = useState("");
  const [busy, setBusy] = useState<null | "pause" | "resume" | "dispute">(null);

  const load = useCallback(async () => {
    if (!episodeId) return;
    setLoading(true);
    setError(null);
    try {
      setEpisode(await getEpisode(episodeId, { token }));
    } catch (err) {
      setError(describeError(err, "No se pudo cargar el episodio"));
    } finally {
      setLoading(false);
    }
  }, [episodeId, token]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Fusión de envíos y decisiones, más recientes primero. El instante de
   * ordenación de un envío es `sentAt` si ya salió y `dueAt` si sigue
   * pendiente — así los futuros programados quedan arriba, que es donde Ops
   * espera ver "lo próximo que va a pasar".
   */
  const timeline = useMemo<TimelineEntry[]>(() => {
    if (!episode) return [];
    const entries: TimelineEntry[] = [
      ...episode.scheduledSends.map<TimelineEntry>((send) => ({
        kind: "send",
        at: send.sentAt ?? send.dueAt,
        send,
      })),
      ...episode.decisionLog.map<TimelineEntry>((decision) => ({
        kind: "decision",
        at: decision.evaluatedAt,
        decision,
      })),
    ];
    return entries.sort(
      (a, b) => new Date(b.at).getTime() - new Date(a.at).getTime(),
    );
  }, [episode]);

  const submitNote = async () => {
    if (!episodeId || savingNote || noteBody.trim() === "") return;
    setSavingNote(true);
    setError(null);
    try {
      await createEpisodeNote(episodeId, { body: noteBody.trim() }, { token });
      setNoteBody("");
      await load();
    } catch (err) {
      setError(describeError(err, "No se pudo guardar la nota"));
    } finally {
      setSavingNote(false);
    }
  };

  const submitPause = async () => {
    if (!episodeId || busy || motivo.trim() === "") return;
    setBusy("pause");
    setError(null);
    try {
      await pauseEpisode(
        episodeId,
        {
          motivo: motivo.trim(),
          // El input datetime-local no lleva zona; se envía como ISO con la
          // offset del navegador, que es lo que exige el contrato.
          hasta: hasta ? new Date(hasta).toISOString() : undefined,
          ticket: ticket.trim() || undefined,
        },
        { token },
      );
      setPauseOpen(false);
      setMotivo("");
      setHasta("");
      setTicket("");
      await load();
    } catch (err) {
      setError(describeError(err, "No se pudo pausar el episodio"));
    } finally {
      setBusy(null);
    }
  };

  const submitResolveDispute = useCallback(async () => {
    if (!episode || disputeReason.trim().length < 3) return;
    setBusy("dispute");
    try {
      await resolveDispute(
        episode.id,
        { desenlace: disputeOutcome, motivo: disputeReason.trim() },
        { token },
      );
      setDisputeOpen(false);
      setDisputeReason("");
      await load();
    } catch (err) {
      window.alert(
        `No se pudo resolver la disputa: ${err instanceof Error ? err.message : String(err)}`,
      );
    } finally {
      setBusy(null);
    }
  }, [episode, disputeOutcome, disputeReason, token, load]);

  const submitResume = async () => {
    if (!episodeId || busy) return;
    setBusy("resume");
    setError(null);
    try {
      await resumeEpisode(episodeId, { token });
      await load();
    } catch (err) {
      setError(describeError(err, "No se pudo reanudar el episodio"));
    } finally {
      setBusy(null);
    }
  };

  // 404 del passthrough: ms-ops no está configurado en este entorno.
  if (unavailable) {
    return <UnavailableNotice />;
  }

  if (loading && !episode) {
    return <Loading label="Cargando episodio…" />;
  }

  if (!episode) {
    return (
      <div style={{ padding: "1.5rem" }}>
        <div
          style={{
            padding: "0.6rem 0.85rem",
            background: COLORS.dangerBg,
            color: COLORS.dangerFg,
            borderRadius: "0.45rem",
            fontSize: ".85rem",
          }}
        >
          {error ?? "Episodio no encontrado"}
        </div>
        <Link
          to="/operations/recobros"
          style={{ display: "inline-block", marginTop: "0.75rem", color: COLORS.link }}
        >
          ← Volver a Recobros
        </Link>
      </div>
    );
  }

  const statusStyle = EPISODE_STATUS_STYLES[episode.status] ?? {
    bg: "#e9ecef",
    fg: "#495057",
  };

  return (
    <div style={{ padding: "1rem 1.5rem", maxWidth: 1200, margin: "0 auto" }}>
      <Link
        to="/operations/recobros"
        style={{
          color: COLORS.muted,
          fontSize: ".8rem",
          textDecoration: "none",
        }}
      >
        ← Recobros
      </Link>

      <div
        style={{
          margin: "0.5rem 0 0.85rem",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "1rem",
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2 style={{ margin: 0, fontSize: "1.15rem", color: COLORS.heading }}>
            {episode.subscriptionRef}
          </h2>
          <div
            style={{
              marginTop: "0.35rem",
              display: "flex",
              gap: "0.35rem",
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <Badge bg={statusStyle.bg} fg={statusStyle.fg}>
              {EPISODE_STATUS_LABEL[episode.status]}
            </Badge>
            <Badge bg="#f8f9fb" fg="#73879C">
              {PROCESS_KIND_LABEL[episode.processKind]}
            </Badge>
            <Badge bg="#f8f9fb" fg="#73879C">
              {episode.market}
            </Badge>
            <Badge bg={episode.brokerId ? "#e7f1ff" : "#f8f9fb"} fg={episode.brokerId ? "#0069d9" : "#73879C"}>
              {episode.brokerId ? "Broker" : "D2C"}
            </Badge>
            {episode.disputed && (
              <>
                <Badge bg="#fff3cd" fg="#856404">
                  En disputa
                </Badge>
                <button
                  type="button"
                  onClick={() => setDisputeOpen((v) => !v)}
                  disabled={busy !== null}
                  style={{
                    border: "1px solid #856404",
                    color: "#856404",
                    background: "transparent",
                    borderRadius: ".4rem",
                    padding: ".1rem .5rem",
                    fontSize: ".75rem",
                    cursor: "pointer",
                  }}
                  title="La cadencia está congelada por el chargeback: al resolver, el motor vuelve a decidir"
                >
                  Resolver disputa…
                </button>
              </>
            )}
          </div>
        </div>

        <div style={{ display: "flex", gap: "0.5rem" }}>
          {episode.status === "paused" ? (
            <button
              type="button"
              onClick={submitResume}
              disabled={busy !== null}
              style={buttonStyle}
            >
              {busy === "resume" ? "Reanudando…" : "Reanudar"}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setPauseOpen((v) => !v)}
              disabled={busy !== null || episode.status === "closed"}
              title={
                episode.status === "closed"
                  ? "El episodio ya está cerrado"
                  : "Pausar la cadencia de este episodio"
              }
              style={{
                ...buttonStyle,
                background: "#856404",
                borderColor: "#856404",
                cursor: episode.status === "closed" ? "not-allowed" : "pointer",
              }}
            >
              Pausar…
            </button>
          )}
        </div>
      </div>

      {/*
        Acciones de paridad con el Sheets (F2.d): enviar una plantilla, tener
        el link de pago delante y generar el OTP. Van encima del detalle
        porque son lo primero que Ops hace al abrir la ficha; el historial y
        las notas quedan debajo como contexto de esa decisión.
      */}
      <ActionsBar episode={episode} token={token} onSent={load} />

      {error && (
        <div
          style={{
            padding: "0.6rem 0.85rem",
            background: COLORS.dangerBg,
            color: COLORS.dangerFg,
            borderRadius: "0.45rem",
            marginBottom: "0.65rem",
            fontSize: ".82rem",
          }}
        >
          {error}
        </div>
      )}

      {disputeOpen && (
          <div
            style={{
              marginTop: "0.75rem",
              padding: "0.75rem",
              background: "#fffaf0",
              border: "1px solid #f0c36d",
              borderRadius: "0.45rem",
              display: "grid",
              gap: "0.55rem",
              maxWidth: 520,
            }}
          >
            <div style={{ fontSize: ".85rem" }}>
              <strong>Resolver la disputa.</strong> La cadencia está congelada mientras
              el chargeback esté vivo. Al resolver, el motor vuelve a decidir: si ya no
              hay deuda, cerrará el caso; si la disputa se perdió, la reclamación sigue
              (y la vía de cobro es reemitir el recibo).
            </div>
            <label style={{ display: "grid", gap: ".25rem" }}>
              <span style={labelStyle}>¿Cómo acabó?</span>
              <select
                value={disputeOutcome}
                onChange={(e) =>
                  setDisputeOutcome(
                    e.target.value as "ganada" | "perdida" | "reemitida" | "sin_disputa",
                  )
                }
                style={inputStyle}
              >
                <option value="ganada">Ganada — el cobro se sostiene</option>
                <option value="perdida">Perdida — chargeback consumado, la deuda renace</option>
                <option value="reemitida">Ya reemitida — se emitió factura nueva</option>
                <option value="sin_disputa">Marcada por error</option>
              </select>
            </label>
            <label style={{ display: "grid", gap: ".25rem" }}>
              <span style={labelStyle}>Motivo</span>
              <input
                type="text"
                value={disputeReason}
                onChange={(e) => setDisputeReason(e.target.value)}
                placeholder="p.ej. el banco resolvió a nuestro favor el 08/08"
                style={inputStyle}
              />
            </label>
            <div style={{ display: "flex", gap: ".5rem" }}>
              <button
                type="button"
                onClick={submitResolveDispute}
                disabled={disputeReason.trim().length < 3 || busy !== null}
                style={{ ...buttonStyle, opacity: disputeReason.trim().length >= 3 ? 1 : 0.5 }}
              >
                {busy === "dispute" ? "Resolviendo…" : "Resolver disputa"}
              </button>
              <button
                type="button"
                onClick={() => setDisputeOpen(false)}
                disabled={busy !== null}
                style={secondaryButtonStyle}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {pauseOpen && (
        <Card
          style={{
            padding: "0.75rem 0.95rem",
            marginBottom: "0.75rem",
            borderLeft: "3px solid #856404",
          }}
        >
          <div style={{ marginBottom: "0.5rem" }}>
            <strong style={{ color: "#856404" }}>Pausar cadencia</strong>
            <div style={{ fontSize: ".75rem", color: COLORS.muted }}>
              El motivo es obligatorio: una pausa sin motivo no se puede
              revisar tres meses después. Sin fecha «hasta» la pausa es
              indefinida.
            </div>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "0.75rem",
              alignItems: "flex-end",
            }}
          >
            <label style={{ ...fieldStyle, flex: "1 1 320px" }}>
              <span style={labelStyle}>Motivo *</span>
              <input
                type="text"
                value={motivo}
                maxLength={2000}
                placeholder="Cliente en proceso de reclamación"
                onChange={(e) => setMotivo(e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={fieldStyle}>
              <span style={labelStyle}>Hasta (opcional)</span>
              <input
                type="datetime-local"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                style={inputStyle}
              />
            </label>
            <label style={fieldStyle}>
              <span style={labelStyle}>Ticket HubSpot</span>
              <input
                type="text"
                value={ticket}
                maxLength={128}
                onChange={(e) => setTicket(e.target.value)}
                style={inputStyle}
              />
            </label>
            <button
              type="button"
              onClick={submitPause}
              disabled={busy !== null || motivo.trim() === ""}
              style={{
                ...buttonStyle,
                background: "#856404",
                borderColor: "#856404",
                opacity: motivo.trim() === "" ? 0.6 : 1,
              }}
            >
              {busy === "pause" ? "Pausando…" : "Pausar"}
            </button>
            <button
              type="button"
              onClick={() => setPauseOpen(false)}
              style={secondaryButtonStyle}
            >
              Cancelar
            </button>
          </div>
        </Card>
      )}

      <Card style={{ padding: "0.75rem 0.95rem", marginBottom: "0.75rem" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "0.75rem",
          }}
        >
          <Field label="Recibos pendientes" value={String(episode.pendingInvoicesCount)} />
          <Field label="Paso actual" value={episode.currentStep ?? "—"} mono />
          {/*
            El CLIENTE, no un ULID muerto: desde el episodio se llega a su
            ficha en un clic. Era el peor callejón sin salida del panel — la
            operadora veía tres recibos impagados, quería llamar, y tenía que
            copiar el identificador a mano y pegarlo en la búsqueda global.
          */}
          <div>
            <div style={labelStyle}>Cliente</div>
            {episode.leadUlid ? (
              <Link
                to={`/leads/${episode.leadUlid}`}
                style={{ color: COLORS.link, fontFamily: "monospace", fontSize: ".85rem" }}
              >
                {episode.leadUlid.slice(0, 8)}… ↗
              </Link>
            ) : (
              <div style={{ fontSize: ".85rem", color: COLORS.muted }}>—</div>
            )}
          </div>
          <Field label="Abierto" value={fmtDateTime(episode.openedAt)} />
          <Field label="Cerrado" value={fmtDateTime(episode.closedAt)} />
          <Field label="Motivo de cierre" value={episode.closeReason ?? "—"} />
          <Field
            label="Última conciliación"
            value={fmtDateTime(episode.lastReconciledAt)}
          />
          {/*
            El episodio es el contenedor; la TAREA es el trabajo pendiente que
            genera. Sin este enlace, quien investigaba un caso no podía
            ejecutarlo: tenía que volver a la cola y buscarlo a mano.
          */}
          <div>
            <div style={labelStyle}>Trabajo pendiente</div>
            <Link
              to={`/operations/recobros/tareas?episodeId=${episode.id}`}
              style={{ color: COLORS.link, fontSize: ".85rem" }}
            >
              Ver sus tareas →
            </Link>
          </div>
          <Field label="Episodio" value={episode.id} mono />
        </div>
      </Card>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "minmax(0, 3fr) minmax(0, 2fr)",
          gap: "0.75rem",
          alignItems: "start",
        }}
      >
        <Card style={{ padding: "0.75rem 0.95rem" }}>
          <h3 style={sectionTitle}>Historial</h3>
          {timeline.length === 0 ? (
            <p style={{ color: COLORS.muted, fontSize: ".85rem" }}>
              Sin envíos ni decisiones registradas todavía.
            </p>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {timeline.map((entry) => (
                <TimelineRow
                  key={
                    entry.kind === "send"
                      ? `s-${entry.send.id}`
                      : `d-${entry.decision.id}`
                  }
                  entry={entry}
                />
              ))}
            </ul>
          )}
        </Card>

        <Card style={{ padding: "0.75rem 0.95rem" }}>
          <h3 style={sectionTitle}>Notas ({episode.notes.length})</h3>

          <div style={{ marginBottom: "0.75rem" }}>
            <textarea
              value={noteBody}
              maxLength={10000}
              rows={3}
              placeholder="Añadir una observación…"
              onChange={(e) => setNoteBody(e.target.value)}
              style={{
                ...inputStyle,
                width: "100%",
                resize: "vertical",
                fontFamily: "inherit",
              }}
            />
            <button
              type="button"
              onClick={submitNote}
              disabled={savingNote || noteBody.trim() === ""}
              style={{
                ...buttonStyle,
                marginTop: "0.4rem",
                opacity: noteBody.trim() === "" ? 0.6 : 1,
              }}
            >
              {savingNote ? "Guardando…" : "Añadir nota"}
            </button>
          </div>

          {episode.notes.length === 0 ? (
            <p style={{ color: COLORS.muted, fontSize: ".85rem" }}>
              Sin notas.
            </p>
          ) : (
            <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
              {episode.notes.map((note) => (
                <li
                  key={note.id}
                  style={{
                    padding: "0.5rem 0",
                    borderTop: `1px solid ${COLORS.rowBorder}`,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: "0.5rem",
                      fontSize: ".72rem",
                      color: COLORS.muted,
                    }}
                  >
                    <strong>{note.author}</strong>
                    <span>{fmtDateTime(note.createdAt)}</span>
                  </div>
                  <div
                    style={{
                      fontSize: ".85rem",
                      color: COLORS.ink,
                      whiteSpace: "pre-wrap",
                    }}
                  >
                    {note.body}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

function TimelineRow({ entry }: { entry: TimelineEntry }) {
  if (entry.kind === "send") {
    const { send } = entry;
    const style = SEND_STATUS_STYLES[send.status] ?? {
      bg: "#e9ecef",
      fg: "#495057",
    };
    return (
      <li style={timelineItem}>
        <div style={timelineHead}>
          <Badge bg={style.bg} fg={style.fg}>
            {SEND_STATUS_LABEL[send.status] ?? send.status}
          </Badge>
          <strong style={{ fontSize: ".85rem", color: COLORS.ink }}>
            {CHANNEL_LABEL[send.channel] ?? send.channel}
          </strong>
          <span style={{ fontSize: ".72rem", color: COLORS.muted }}>
            {fmtDateTime(entry.at)}
          </span>
        </div>
        <div style={{ fontSize: ".78rem", color: COLORS.muted }}>
          {send.templateId ?? "sin plantilla"}
          {send.stepId ? ` · paso ${send.stepId}` : ""}
        </div>
        {send.failureReason && (
          <div style={{ fontSize: ".78rem", color: COLORS.dangerFg }}>
            {send.failureReason}
          </div>
        )}
      </li>
    );
  }

  const { decision } = entry;
  const style = DECISION_STYLES[decision.decision] ?? {
    bg: "#f8f9fb",
    fg: "#73879C",
  };
  return (
    <li style={timelineItem}>
      <div style={timelineHead}>
        <Badge bg={style.bg} fg={style.fg}>
          {DECISION_LABEL[decision.decision as DecisionKind] ??
            decision.decision}
        </Badge>
        {decision.dryRun && (
          <Badge
            bg="#f8f9fb"
            fg="#73879C"
            title="Decisión evaluada sin ejecutar (modo shadow)"
          >
            dry-run
          </Badge>
        )}
        <span style={{ fontSize: ".72rem", color: COLORS.muted }}>
          {fmtDateTime(entry.at)}
        </span>
      </div>
      <div style={{ fontSize: ".78rem", color: COLORS.ink }}>
        {decision.reason}
      </div>
    </li>
  );
}

function Field({
  label,
  value,
  mono,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div>
      <div style={labelStyle}>{label}</div>
      <div
        style={{
          fontSize: ".85rem",
          color: COLORS.ink,
          fontFamily: mono ? "monospace" : "inherit",
          wordBreak: "break-all",
        }}
      >
        {value}
      </div>
    </div>
  );
}

const sectionTitle: React.CSSProperties = {
  margin: "0 0 0.5rem",
  fontSize: ".9rem",
  color: COLORS.heading,
};

const timelineItem: React.CSSProperties = {
  padding: "0.5rem 0",
  borderTop: `1px solid ${COLORS.rowBorder}`,
};

const timelineHead: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: "0.4rem",
  flexWrap: "wrap",
  marginBottom: "0.15rem",
};
